/**
 * Owner: Masa Bayu
 * File: platform-access-authorize.ts
 * Date:Time: 2026-09-23 15:52
 * Description: Remediation auth — server identity, existing QXW/QXRead/ADAM authorities.
 */
import { validateQXWDocument } from '@qx/qxw-core';
import { ADAM_QXW_MUTATION_PATH } from './adam-feature-entitlement-proposal';
import { verifyAdamFeatureEntitlement, } from './adam-feature-entitlement-authorize';
import { ADAM_FEATURE_ENTITLEMENT_SOT } from './adam-feature-entitlement-sot';
import { assertNoForbiddenFoundationKey } from './authority';
import { authorizeReaderLaunch } from './entitlement-boundary';
import { authorizeQxCl3Command, } from './product-lifecycle-authorize';
import { appendQxPlatformAccessAudit, createQxPlatformAccessAuditLog, } from './platform-access-audit';
import { qxPlatformFeatureAccessAllowed, } from './platform-access-matrix';
import { findCapabilityAssignment, projectAccountToCl3Actor, } from './platform-access-principals';
import { callerSuppliesAuthority, readServerIdentity, } from './platform-access-server';
export const QX_PLATFORM_ADAM_ENTITLEMENT_SOT = Object.freeze({
    status: 'OWNER_LOCKED',
    track: 'ADAM-ENTITLEMENT-COMPLETION',
    sotId: ADAM_FEATURE_ENTITLEMENT_SOT.id,
    silentCompletionForbidden: true,
});
/** Registered QXW validation authority. Not a host-supplied function. */
export const QX_PLATFORM_QXW_VALIDATOR = validateQXWDocument;
export const QX_PLATFORM_QXW_PATH = ADAM_QXW_MUTATION_PATH;
export const QX_PLATFORM_QXW_SOURCE = 'existing_qxw_validator';
const BYPASS_DENY = 'Full Access ≠ authority bypass · use official validation / publication paths';
function audited(server, input) {
    return appendQxPlatformAccessAudit(createQxPlatformAccessAuditLog(), {
        accountId: input.accountId,
        feature: input.feature,
        decision: input.decision,
        reason: input.reason,
        at: server.observedAt,
        ...(input.bypassAttempted === true ? { bypassAttempted: true } : {}),
    });
}
function assignmentFor(server) {
    return findCapabilityAssignment(server.assignments, server.account.accountId);
}
export function authorizeQxPlatformFeatureAccess(input) {
    assertNoForbiddenFoundationKey(input, 'QX Platform Access feature auth');
    const identity = readServerIdentity(input.server);
    if (!identity.ok) {
        return Object.freeze({
            ok: false,
            reason: identity.reason,
            bypassAttempted: false,
            feature: input.feature,
            audit: audited(input.server, {
                accountId: input.server.session.accountId,
                feature: input.feature,
                decision: 'deny',
                reason: identity.reason,
            }),
        });
    }
    const callerKey = callerSuppliesAuthority(input);
    if (callerKey !== null) {
        const reason = `caller ${callerKey} ──X──> authority`;
        return Object.freeze({
            ok: false,
            reason,
            bypassAttempted: true,
            feature: input.feature,
            audit: audited(input.server, {
                accountId: input.server.account.accountId,
                feature: input.feature,
                decision: 'deny',
                reason,
                bypassAttempted: true,
            }),
        });
    }
    if (input.requestBypass !== undefined) {
        return Object.freeze({
            ok: false,
            reason: BYPASS_DENY,
            bypassAttempted: true,
            feature: input.feature,
            audit: audited(input.server, {
                accountId: input.server.account.accountId,
                feature: input.feature,
                decision: 'deny',
                reason: BYPASS_DENY,
                bypassAttempted: true,
            }),
        });
    }
    const assignment = assignmentFor(input.server);
    if (!qxPlatformFeatureAccessAllowed(assignment, input.feature)) {
        const reason = 'capability not assigned on existing account';
        return Object.freeze({
            ok: false,
            reason,
            bypassAttempted: false,
            feature: input.feature,
            audit: audited(input.server, {
                accountId: input.server.account.accountId,
                feature: input.feature,
                decision: 'deny',
                reason,
            }),
        });
    }
    const reason = `allow ${input.feature}`;
    return Object.freeze({
        ok: true,
        accountId: input.server.account.accountId,
        feature: input.feature,
        bypassAttempted: false,
        audit: audited(input.server, {
            accountId: input.server.account.accountId,
            feature: input.feature,
            decision: 'allow',
            reason,
        }),
    });
}
export function authorizeQxPlatformWriterCommand(input) {
    const callerKey = callerSuppliesAuthority(input);
    const feature = authorizeQxPlatformFeatureAccess({
        server: input.server,
        feature: 'qxwriter',
        ...(callerKey !== null ? { [callerKey]: 'rejected' } : {}),
    });
    if (!feature.ok) {
        return Object.freeze({
            ok: false,
            reason: feature.reason,
            platformFeature: 'qxwriter',
            qxwPath: null,
            qxwSource: null,
            qxwValidator: 'validateQXWDocument',
            qxwErrorCount: null,
            cl3IsQxwAuthority: false,
            audit: feature.audit,
        });
    }
    if ('qxwValidate' in input.server) {
        const reason = 'host-supplied qxwValidate is not the QXW validation authority';
        return Object.freeze({
            ok: false,
            reason,
            platformFeature: 'qxwriter',
            qxwPath: null,
            qxwSource: null,
            qxwValidator: 'validateQXWDocument',
            qxwErrorCount: null,
            cl3IsQxwAuthority: false,
            audit: audited(input.server, {
                accountId: input.server.account.accountId,
                feature: 'qxwriter',
                decision: 'deny',
                reason,
                bypassAttempted: true,
            }),
        });
    }
    const validated = QX_PLATFORM_QXW_VALIDATOR(input.server.qxwDocument);
    if (!validated.ok) {
        const reason = 'validateQXWDocument rejected the document';
        return Object.freeze({
            ok: false,
            reason,
            platformFeature: 'qxwriter',
            qxwPath: null,
            qxwSource: QX_PLATFORM_QXW_SOURCE,
            qxwValidator: 'validateQXWDocument',
            qxwErrorCount: validated.errors.length,
            cl3IsQxwAuthority: false,
            audit: audited(input.server, {
                accountId: input.server.account.accountId,
                feature: 'qxwriter',
                decision: 'deny',
                reason,
            }),
        });
    }
    const result = authorizeQxCl3Command({
        actor: projectAccountToCl3Actor(input.server.account),
        record: input.record,
        command: input.command,
    });
    const reason = result.ok
        ? 'allow qxwriter via existing validation and CL3 actor authority'
        : result.reason;
    return Object.freeze({
        ...result,
        platformFeature: 'qxwriter',
        qxwPath: QX_PLATFORM_QXW_PATH.id,
        qxwSource: QX_PLATFORM_QXW_SOURCE,
        qxwValidator: 'validateQXWDocument',
        qxwErrorCount: 0,
        cl3IsQxwAuthority: false,
        audit: audited(input.server, {
            accountId: input.server.account.accountId,
            feature: 'qxwriter',
            decision: result.ok ? 'allow' : 'deny',
            reason,
        }),
    });
}
export function authorizeQxPlatformReaderAccess(input) {
    const callerKey = callerSuppliesAuthority(input);
    const feature = authorizeQxPlatformFeatureAccess({
        server: input.server,
        feature: 'qxread',
        ...(callerKey !== null ? { [callerKey]: 'rejected' } : {}),
    });
    if (!feature.ok)
        return feature;
    const launch = authorizeReaderLaunch({
        sessionAuthenticated: input.server.session.state === 'authenticated',
        accountId: input.server.account.accountId,
        publicationBound: input.server.publicationBound,
        entitlement: input.server.readerEntitlement,
    });
    if (!launch.allowed) {
        return Object.freeze({
            ok: false,
            reason: launch.reason,
            bypassAttempted: false,
            feature: 'qxread',
            audit: audited(input.server, {
                accountId: input.server.account.accountId,
                feature: 'qxread',
                decision: 'deny',
                reason: launch.reason,
            }),
        });
    }
    const reason = 'allow qxread via existing reader authority';
    return Object.freeze({
        ok: true,
        accountId: input.server.account.accountId,
        feature: 'qxread',
        bypassAttempted: false,
        audit: audited(input.server, {
            accountId: input.server.account.accountId,
            feature: 'qxread',
            decision: 'allow',
            reason,
        }),
    });
}
export function authorizeQxPlatformAdamInteraction(input) {
    assertNoForbiddenFoundationKey(input, 'QX Platform Access ADAM');
    const callerKey = callerSuppliesAuthority(input);
    if (callerKey !== null) {
        const reason = `caller ${callerKey} ──X──> ADAM authority`;
        return Object.freeze({
            ok: false,
            surfaceAllowed: false,
            entitlementSot: QX_PLATFORM_ADAM_ENTITLEMENT_SOT,
            proposalsAvailable: false,
            reason,
            audit: audited(input.server, {
                accountId: input.server.account.accountId,
                feature: 'adam',
                decision: 'deny',
                reason,
                bypassAttempted: true,
            }),
        });
    }
    const surface = authorizeQxPlatformFeatureAccess({
        server: input.server,
        feature: 'adam',
    });
    if (!surface.ok) {
        return Object.freeze({
            ok: false,
            surfaceAllowed: false,
            entitlementSot: QX_PLATFORM_ADAM_ENTITLEMENT_SOT,
            proposalsAvailable: false,
            reason: surface.reason,
            audit: surface.audit,
        });
    }
    const verify = verifyAdamFeatureEntitlement({
        accountId: input.server.account.accountId,
        source: input.server.adamSource,
    });
    return Object.freeze({
        ok: true,
        surfaceAllowed: true,
        entitlementSot: QX_PLATFORM_ADAM_ENTITLEMENT_SOT,
        proposalsAvailable: verify.proposalsAvailable,
        reason: verify.proposalsAvailable
            ? 'ADAM proposals follow trusted server entitlement state'
            : 'ADAM surface allowed · server entitlement OFF · Core remains usable',
        audit: audited(input.server, {
            accountId: input.server.account.accountId,
            feature: 'adam',
            decision: 'allow',
            reason: verify.reason,
        }),
    });
}
//# sourceMappingURL=platform-access-authorize.js.map